/**
 * Parent-issued auth session (FarmaHub iframe).
 *
 * The FarmaHub shell signs a short-lived JWT (claims include the user's
 * `email`) and hands it to this iframe via `postMessage`. We keep the token
 * ONLY in memory — never in localStorage/URL — so that copying the iframe URL
 * and opening it standalone yields no token, which the backend then rejects
 * with 401. That is the gate: the page cannot function outside the iframe.
 *
 * Handshake:
 *   1. iframe → parent : { type: "PDE_READY" }   (we're mounted, send the token)
 *   2. parent → iframe : { type: "PDE_AUTH", token: "<jwt>" }
 *
 * The parent may re-post PDE_AUTH at any time to refresh an expiring token.
 */

const PARENT_ORIGINS: string[] = (import.meta.env.VITE_PARENT_ORIGIN ?? "")
  .split(",")
  .map((s: string) => s.trim())
  .filter(Boolean);

export type AuthStatus = "waiting" | "ready" | "denied";

const READY_TIMEOUT_MS = 10_000;

let token: string | null = null;
let status: AuthStatus = "waiting";
let initialized = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function getToken(): string | null {
  return token;
}

export function getAuthStatus(): AuthStatus {
  return status;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setToken(next: string): void {
  token = next;
  status = "ready";
  emit();
}

function deny(): void {
  token = null;
  status = "denied";
  emit();
}

function originAllowed(origin: string): boolean {
  // No allow-list configured → dev mode: accept any origin.
  if (PARENT_ORIGINS.length === 0) return true;
  return PARENT_ORIGINS.includes(origin);
}

/**
 * Wire up the postMessage handshake. Safe to call more than once (idempotent).
 */
export function initAuth(): void {
  if (initialized) return;
  initialized = true;

  // Not embedded in a parent frame (standalone open) → no handshake possible.
  if (window.parent === window) {
    deny();
    return;
  }

  window.addEventListener("message", (event: MessageEvent) => {
    if (!originAllowed(event.origin)) return;
    const data = event.data as { type?: unknown; token?: unknown } | null;
    if (
      data &&
      data.type === "PDE_AUTH" &&
      typeof data.token === "string" &&
      data.token.length > 0
    ) {
      setToken(data.token);
    }
  });

  // Announce readiness so the parent knows to send (or resend) the token.
  const targets = PARENT_ORIGINS.length ? PARENT_ORIGINS : ["*"];
  for (const target of targets) {
    window.parent.postMessage({ type: "PDE_READY" }, target);
  }

  // Give up waiting so the user is not stuck on a spinner forever.
  window.setTimeout(() => {
    if (status === "waiting") deny();
  }, READY_TIMEOUT_MS);
}
